<script setup>
import { ref } from 'vue';
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from 'reka-ui';
import { ChevronDown16 } from '@carbon/icons-vue';

// The picker at the top of tier 2: which assistant, which portal. The list under it is the one the page header
// used to open (AssistantSwitcher, PortalSwitcher); the menu below the picker belongs to what is picked.
// The list floats in a layer of its own (L-05): tier 2 clips what overflows it, and the list (27.5rem) is wider
// than the column. A click outside or Escape closes it; Escape gives the focus back to the picker.
defineProps({
  label: { type: String, required: true },
  ariaLabel: { type: String, required: true },
  status: { type: String, default: '' },
  // Chip de status dentro do botão (Polaris AI: Live/Setup do assistente, D-P2).
  badge: { type: Object, default: null },
});

const isOpen = ref(false);
const close = () => {
  isOpen.value = false;
};
</script>

<template>
  <div class="grid grid-cols-[minmax(0,1fr)] gap-2">
    <PopoverRoot v-model:open="isOpen">
      <!-- the accessible name keeps the visible one (what is picked) after the kind of picker, read only by
           screen readers: "Assistant: <name>" -->
      <PopoverTrigger as-child>
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-polaris-md border border-solid border-polaris-input bg-polaris-background px-3 h-9 type-polaris-body font-medium text-polaris-foreground hover:bg-polaris-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
        >
          <span class="sr-only">{{ `${ariaLabel}: ` }}</span>
          <span class="flex-1 min-w-0 truncate text-start">{{ label }}</span>
          <span
            v-if="badge"
            class="shrink-0 rounded-polaris-md px-1.5 py-0.5 type-polaris-caption font-medium"
            :class="
              badge.tone === 'live'
                ? 'bg-polaris-success text-polaris-success-foreground'
                : 'bg-polaris-warning text-polaris-warning-foreground'
            "
          >
            {{ badge.label }}
          </span>
          <ChevronDown16
            aria-hidden="true"
            class="shrink-0 text-polaris-muted-foreground"
          />
        </button>
      </PopoverTrigger>
      <PopoverPortal>
        <!-- the lists are drawn `absolute`; `static` here lets the layer measure them to keep them on screen -->
        <PopoverContent
          side="bottom"
          align="start"
          :side-offset="4"
          :collision-padding="8"
          class="z-50 outline-none [&>*]:!static [&>*]:!max-w-[calc(100vw-1rem)]"
        >
          <slot :close="close" />
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
    <p
      v-if="status"
      class="px-1 type-polaris-caption text-polaris-muted-foreground"
    >
      {{ status }}
    </p>
  </div>
</template>
